import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useSession } from '~/components/contexts/SessionProvider';
import { GET_REFERRALS_CONFIG } from '~/services/referrals';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

const Referrals: React.FC = () => {
  const { t } = useTranslation('referrals');
  const { owner } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    data: referralsData,
    loading: referralsLoading,
    error: referralsError,
    refetch: refetchReferrals,
  } = useQuery(GET_REFERRALS_CONFIG, {
    errorPolicy: 'all',
    skip: !owner.allow_referral, // Only fetch if referrals are enabled
  });

  // Generate referral code from owner ID (first 8 characters)
  const referralCode = owner.referral_code;
  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  const handleStripeAction = async () => {
    if (!referralsData?.get_referrals_config?.data?.loginLink) return;

    setIsLoading(true);
    try {
      // Open Stripe link in new tab
      window.open(referralsData.get_referrals_config.data.loginLink, '_blank');
    } catch (error) {
      console.error('Error opening Stripe link:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Check if user has referrals enabled
  if (!owner.allow_referral) {
    return (
      <div className="hero bg-base-100 min-h-screen">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <div className="avatar placeholder mb-6">
              <div className="bg-neutral text-neutral-content w-16 rounded-full">
                <span className="text-2xl">💰</span>
              </div>
            </div>
            <h1 className="mb-4 text-2xl font-bold">
              {t('not_invited_title')}
            </h1>
            <p className="text-base-content/70 mb-6">
              {t('not_invited_description')}
            </p>
            <button className="btn btn-primary btn-wide rounded-full">
              {t('contact_support')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (referralsLoading) {
    return (
      <div className="hero bg-base-100 min-h-screen">
        <div className="hero-content">
          <div className="loading loading-spinner loading-lg text-primary"></div>
        </div>
      </div>
    );
  }

  if (referralsError) {
    return (
      <div className="hero bg-base-100 min-h-screen">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <div className="avatar placeholder mb-6">
              <div className="bg-error/20 text-error w-16 rounded-full">
                <span className="text-2xl">⚠️</span>
              </div>
            </div>
            <h1 className="mb-4 text-2xl font-bold">{t('error_title')}</h1>
            <p className="text-base-content/70 mb-6">
              {t('error_description')}
            </p>
            <button
              className="btn btn-primary btn-wide rounded-full"
              onClick={() => refetchReferrals()}
            >
              {t('retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const config = referralsData?.get_referrals_config?.data;
  const needsOnboarding = config?.needsOnboarding;

  return (
    <div className="bg-base-100 min-h-screen p-4">
      <div className="mx-auto max-w-4xl">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="hero from-primary/10 to-secondary/10 mb-8 rounded-3xl bg-gradient-to-br"
        >
          <div className="hero-content py-12 text-center">
            <div className="max-w-md">
              <h1 className="mb-4 text-3xl font-bold">{t('welcome_title')}</h1>
              <p className="text-base-content/70 mb-6">
                {t('welcome_description')}
              </p>
              <div className="alert alert-success">
                <span className="text-lg">✅</span>
                <div className="text-left">
                  <h3 className="font-semibold">{t('invitation_confirmed')}</h3>
                  <p className="text-sm opacity-90">
                    {t('invitation_confirmed_description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card bg-base-100 border-base-300 mb-6 border shadow-xl"
        >
          <div className="card-body">
            <h2 className="card-title mb-4 items-center text-2xl">
              <span className="text-2xl">🔗</span>
              {t('your_referral_code')}
            </h2>

            <div className="stats bg-base-200 mb-4 shadow">
              <div className="stat">
                <div className="stat-title">{t('referral_code')}</div>
                <div className="stat-value text-primary font-mono">
                  {referralCode}
                </div>
                <div className="stat-desc">{t('share_this_code')}</div>
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">
                  {t('referral_link')}
                </span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="input input-bordered bg-base-200 flex-1 cursor-default select-all font-mono text-sm"
                  tabIndex={-1}
                />
                <button
                  className="btn btn-primary rounded-full px-6"
                  onClick={handleCopyReferralLink}
                  disabled={copied}
                >
                  {copied ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      {t('copied')}
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {t('copy')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card bg-base-100 border-base-300 mb-6 border shadow-xl"
        >
          <div className="card-body">
            <h2 className="card-title mb-4 items-center text-2xl">
              <span className="text-2xl">💳</span>
              {t('stripe_account_status')}
            </h2>

            {config && (
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="stat bg-base-200 rounded-2xl">
                  <div className="stat-title">
                    {t('account_details_submitted')}
                  </div>
                  <div className="stat-value text-lg">
                    <div
                      className={`badge badge-lg ${
                        config.account.details_submitted
                          ? 'badge-success'
                          : 'badge-warning'
                      }`}
                    >
                      {config.account.details_submitted
                        ? t('completed')
                        : t('pending')}
                    </div>
                  </div>
                </div>

                <div className="stat bg-base-200 rounded-2xl">
                  <div className="stat-title">{t('charges_enabled')}</div>
                  <div className="stat-value text-lg">
                    <div
                      className={`badge badge-lg ${
                        config.account.charges_enabled
                          ? 'badge-success'
                          : 'badge-warning'
                      }`}
                    >
                      {config.account.charges_enabled
                        ? t('enabled')
                        : t('disabled')}
                    </div>
                  </div>
                </div>

                <div className="stat bg-base-200 rounded-2xl">
                  <div className="stat-title">{t('payouts_enabled')}</div>
                  <div className="stat-value text-lg">
                    <div
                      className={`badge badge-lg ${
                        config.account.payouts_enabled
                          ? 'badge-success'
                          : 'badge-warning'
                      }`}
                    >
                      {config.account.payouts_enabled
                        ? t('enabled')
                        : t('disabled')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8 text-center"
        >
          <button
            className={`btn btn-lg btn-wide ${
              needsOnboarding ? 'btn-primary' : 'btn-accent'
            } gap-3 rounded-full shadow-lg`}
            onClick={handleStripeAction}
            disabled={isLoading || !config?.loginLink}
          >
            {isLoading && (
              <span className="loading loading-spinner loading-sm"></span>
            )}
            <span>💰</span>
            {needsOnboarding ? t('complete_setup') : t('open_dashboard')}
          </button>

          <p className="text-base-content/70 mx-auto mt-4 max-w-md">
            {needsOnboarding
              ? t('complete_setup_description')
              : t('open_dashboard_description')}
          </p>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card bg-base-100 border-base-300 border shadow-xl"
        >
          <div className="card-body">
            <h2 className="card-title mb-6 items-center text-2xl">
              <span className="text-2xl">💡</span>
              {t('how_it_works')}
            </h2>

            <div className="steps steps-vertical lg:steps-horizontal">
              <div className="step step-primary">
                <div className="text-left">
                  <h3 className="text-lg font-bold">{t('step_1_title')}</h3>
                  <p className="text-base-content/70 mt-1">
                    {t('step_1_description')}
                  </p>
                </div>
              </div>

              <div className="step step-primary">
                <div className="text-left">
                  <h3 className="text-lg font-bold">{t('step_2_title')}</h3>
                  <p className="text-base-content/70 mt-1">
                    {t('step_2_description')}
                  </p>
                </div>
              </div>

              <div className="step step-primary">
                <div className="text-left">
                  <h3 className="text-lg font-bold">{t('step_3_title')}</h3>
                  <p className="text-base-content/70 mt-1">
                    {t('step_3_description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Referrals;
